/**
 * RemediationBadges — Shows owner role, complexity level, and estimated effort as badges.
 *
 * Uses visual indicators (colors) to quickly communicate remediation metadata.
 */

interface RemediationBadgesProps {
  ownerRole: string; // "content_editor" | "designer" | "developer" | "mixed_team"
  fixComplexity: string; // "easy" | "moderate" | "complex"
  estimatedEffort: string; // effort estimate string
  className?: string;
}

const OWNER_ROLE_LABELS: Record<string, string> = {
  content_editor: "Content Editor",
  designer: "Designer",
  developer: "Developer",
  mixed_team: "Mixed Team",
};

const COMPLEXITY_LABELS: Record<string, string> = {
  easy: "Easy",
  moderate: "Moderate",
  complex: "Complex",
};

const EFFORT_LABELS: Record<string, string> = {
  "5-15 min": "5–15 min",
  "15-30 min": "15–30 min",
  "30-60 min": "30–60 min",
  "1-4 hours": "1–4 hours",
  "4+ hours": "4+ hours",
  "investigation needed": "Investigation needed",
};

export function RemediationBadges({
  ownerRole,
  fixComplexity,
  estimatedEffort,
  className = "",
}: RemediationBadgesProps) {
  const getComplexityClass = (complexity: string): string => {
    switch (complexity) {
      case "easy":
        return "badge--complexity-easy";
      case "moderate":
        return "badge--complexity-moderate";
      case "complex":
        return "badge--complexity-complex";
      default:
        return "";
    }
  };

  const getEffortClass = (effort: string): string => {
    if (effort.includes("5-15 min") || effort.includes("15-30 min")) {
      return "badge--effort-short";
    }
    if (effort.includes("30-60 min")) {
      return "badge--effort-medium";
    }
    if (effort.includes("1-4 hours")) {
      return "badge--effort-long";
    }
    return "badge--effort-very-long";
  };

  const getOwnerClass = (role: string): string => `badge--owner-${role}`;

  return (
    <div className={`remediation-badges ${className}`}>
      <span
        className={`badge ${getOwnerClass(ownerRole)}`}
        title="Who typically handles this remediation"
      >
        {OWNER_ROLE_LABELS[ownerRole] || ownerRole}
      </span>
      <span
        className={`badge ${getComplexityClass(fixComplexity)}`}
        title="How complex is this fix"
      >
        {COMPLEXITY_LABELS[fixComplexity] || fixComplexity}
      </span>
      <span
        className={`badge ${getEffortClass(estimatedEffort)}`}
        title="Estimated time to fix"
      >
        {EFFORT_LABELS[estimatedEffort] || estimatedEffort}
      </span>
    </div>
  );
}
